import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { GoldenCircles, CornerMark } from "./GeometricDecor";
import { colorsCopy } from "@/lib/content";
import { getLanguageFromPath } from "@/lib/language";

const DEFAULT_COLORS = [
  { hex: "#ddece8" },
  { hex: "#004643" },
  { hex: "#acd1c6" },
  { hex: "#202020" },
];

const COLOR_ROLES = [
  "Háttér / Világos felület",
  "Fő szín / Címek / CTA",
  "Kiemelés / Másodlagos",
  "Szöveg / Kontraszt",
];

function isValidHex(hex: string) {
  return /^#[0-9a-fA-F]{6}$/.test(hex);
}

function parseCoolorsUrl(url: string): string[] | null {
  try {
    const cleaned = url.trim();
    // Match coolors.co/XXXXXX-XXXXXX-... pattern
    const match = cleaned.match(/coolors\.co\/([0-9a-fA-F-]+)/);
    if (!match) return null;
    const parts = match[1].split("-").filter((p) => /^[0-9a-fA-F]{6}$/.test(p));
    if (parts.length < 2) return null;
    return parts.map((p) => `#${p}`);
  } catch {
    return null;
  }
}

function hexToHsl(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function hslToHex(h: number, s: number, l: number) {
  const a = s * Math.min(l, 100 - l) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(color * 2.55).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

function getLuminance(hex: string) {
  if (!isValidHex(hex)) return 0.5;
  const rgb = [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
  const a = rgb.map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

function getContrastRatio(hex1: string, hex2: string) {
  const l1 = getLuminance(hex1);
  const l2 = getLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function getContrastGrade(ratio: number) {
  if (ratio >= 7) return { label: "AAA", color: "#22c55e" };
  if (ratio >= 4.5) return { label: "AA", color: "#84cc16" };
  if (ratio >= 3) return { label: "AA Large", color: "#eab308" };
  return { label: "Fail", color: "#ef4444" };
}

export default function ColorHelperSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const copy = colorsCopy[getLanguageFromPath(location.pathname)];
  const defaultColors = DEFAULT_COLORS.map((color, index) => ({ ...color, ...copy.defaults[index] }));
  const [colors, setColors] = useState(defaultColors);
  const [urlInput, setUrlInput] = useState("https://coolors.co/ddece8-004643-acd1c6-202020");
  const [urlError, setUrlError] = useState("");
  const [urlSuccess, setUrlSuccess] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [activeColor, setActiveColor] = useState(defaultColors[1]);
  const [lightnessShift, setLightnessShift] = useState(0);
  const [satShift, setSatShift] = useState(0);
  const [bgColor, setBgColor] = useState("#ddece8");
  const [textColor, setTextColor] = useState("#004643");

  const applyUrl = useCallback((url: string) => {
    const parsed = parseCoolorsUrl(url);
    if (!parsed) {
      setUrlError(copy.invalid);
      setUrlSuccess(false);
      return;
    }
    const newColors = parsed.slice(0, 6).map((hex, i) => ({
      hex,
      name: copy.colorName(i),
      role: copy.roles[i] ?? copy.fallbackRole,
    }));
    setColors(newColors);
    setActiveColor(newColors[Math.min(1, newColors.length - 1)]);
    setBgColor(newColors[0].hex);
    setTextColor(newColors[Math.min(1, newColors.length - 1)].hex);
    setLightnessShift(0);
    setSatShift(0);
    setUrlError("");
    setUrlSuccess(true);
    setTimeout(() => setUrlSuccess(false), 2500);
  }, [copy]);

  const handleUrlSubmit = () => applyUrl(urlInput);

  const adjustedHex = useCallback(() => {
    const hsl = hexToHsl(activeColor.hex);
    const newL = Math.max(5, Math.min(95, hsl.l + lightnessShift));
    const newS = Math.max(0, Math.min(100, hsl.s + satShift));
    return hslToHex(hsl.h, newS, newL);
  }, [activeColor, lightnessShift, satShift]);

  const copyHex = (hex: string) => {
    navigator.clipboard.writeText(hex);
    setCopied(hex);
    setTimeout(() => setCopied(null), 1500);
  };

  const contrast = getContrastRatio(bgColor, textColor);
  const grade = getContrastGrade(contrast);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const adjusted = adjustedHex();

  // Quick pairs derived from current palette
  const quickPairs = [
    { bg: colors[0]?.hex ?? "#ddece8", text: colors[1]?.hex ?? "#004643", name: copy.quickPair(1, 2) },
    { bg: colors[1]?.hex ?? "#004643", text: colors[2]?.hex ?? "#acd1c6", name: copy.quickPair(2, 3) },
    { bg: colors[3]?.hex ?? "#202020", text: colors[0]?.hex ?? "#ddece8", name: copy.quickPair(4, 1) },
    { bg: colors[2]?.hex ?? "#acd1c6", text: colors[1]?.hex ?? "#004643", name: copy.quickPair(3, 2) },
  ];

  return (
    <section id="colors" ref={sectionRef} className="py-24 md:py-32 relative overflow-hidden" style={{ backgroundColor: "#ddece8" }}>
      <GoldenCircles size={360} opacity={0.06} color="#004643" className="-top-20 -right-20" />
      <GoldenCircles size={220} opacity={0.05} color="#004643" className="bottom-16 -left-12" />
      <CornerMark position="tl" size={28} color="#004643" opacity={0.15} className="top-10 left-10" />
      <CornerMark position="br" size={28} color="#004643" opacity={0.15} className="bottom-10 right-10" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="text-center mb-12">
          <div
            className="reveal opacity-0 translate-y-6 transition-all duration-700 inline-flex items-center gap-2 px-5 py-2 rounded-full border text-xs font-medium tracking-widest uppercase mb-6"
            style={{ borderColor: "rgba(0,70,67,0.25)", color: "#004643" }}
          >
            <i className="ri-palette-line text-sm" />
            {copy.eyebrow}
          </div>
          <h2
            className="reveal opacity-0 translate-y-6 transition-all duration-700 text-3xl md:text-4xl lg:text-5xl font-light leading-tight mb-4"
            style={{ fontFamily: "'League Spartan', sans-serif", color: "#004643" }}
          >
            {copy.titleTop}
            <br />
            <span className="font-bold italic">{copy.titleAccent}</span>
          </h2>
          <p className="reveal opacity-0 translate-y-6 transition-all duration-700 text-gray-500 text-base max-w-xl mx-auto">
            {copy.intro}
          </p>
        </div>

        {/* Coolors URL Input */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 mb-10">
          <div className="max-w-2xl mx-auto rounded-2xl p-6" style={{ backgroundColor: "white", border: "1px solid rgba(0,70,67,0.12)" }}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "#e8f4f1" }}>
                <i className="ri-links-line text-sm" style={{ color: "#004643" }} />
              </div>
              <h3 className="text-sm font-semibold" style={{ color: "#004643" }}>{copy.paste}</h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={urlInput}
                onChange={(e) => { setUrlInput(e.target.value); setUrlError(""); }}
                onKeyDown={(e) => { if (e.key === "Enter") handleUrlSubmit(); }}
                placeholder="https://coolors.co/ddece8-004643-acd1c6-202020"
                className="flex-1 px-4 py-3 rounded-xl border text-sm placeholder-gray-400 focus:outline-none transition-colors font-mono"
                style={{ borderColor: urlError ? "#ef4444" : "#acd1c6", color: "#004643" }}
                onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#004643"; }}
                onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = urlError ? "#ef4444" : "#acd1c6"; }}
              />
              <button
                onClick={handleUrlSubmit}
                className="px-5 py-3 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                style={{ backgroundColor: "#004643", color: "#acd1c6" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#005a56"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#004643"; }}
              >
                <i className="ri-refresh-line" />
                {copy.apply}
              </button>
            </div>
            {urlError && (
              <div className="mt-3 flex items-center gap-2 text-xs text-red-500">
                <i className="ri-error-warning-line" />
                {urlError}
              </div>
            )}
            {urlSuccess && (
              <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: "#004643" }}>
                <i className="ri-checkbox-circle-line" style={{ color: "#22c55e" }} />
                {copy.success}
              </div>
            )}
            <div className="mt-3 flex items-center gap-2">
              <a
                href="https://coolors.co/ddece8-004643-acd1c6-202020"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer transition-opacity hover:opacity-70"
                style={{ color: "#004643" }}
              >
                <i className="ri-external-link-line" />
                {copy.open}
              </a>
              <span className="text-gray-300">·</span>
              <span className="text-xs text-gray-400">{copy.hint}</span>
            </div>
          </div>
        </div>

        {/* Color Cards Grid */}
        <div
          className="reveal opacity-0 translate-y-6 transition-all duration-700 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-12"
        >
          {colors.map((c) => (
            <button
              key={c.hex}
              onClick={() => { setActiveColor(c); setLightnessShift(0); setSatShift(0); }}
              className="relative group rounded-2xl p-4 sm:p-5 text-left transition-all duration-300 cursor-pointer"
              style={{
                backgroundColor: c.hex,
                border: activeColor.hex === c.hex ? "3px solid rgba(0,0,0,0.3)" : "3px solid transparent",
                transform: activeColor.hex === c.hex ? "scale(1.03)" : "scale(1)",
                minHeight: "120px",
              }}
            >
              <div className={`text-xs font-medium uppercase tracking-widest mb-2 ${getLuminance(c.hex) > 0.45 ? "text-gray-600" : "text-white/60"}`}>
                {c.name}
              </div>
              <div className={`text-base font-bold font-mono mb-1 ${getLuminance(c.hex) > 0.45 ? "text-gray-800" : "text-white"}`}>
                {c.hex.toUpperCase()}
              </div>
              <div className={`text-xs leading-snug ${getLuminance(c.hex) > 0.45 ? "text-gray-500" : "text-white/50"}`}>
                {c.role}
              </div>
              <div
                className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                style={{ backgroundColor: getLuminance(c.hex) > 0.45 ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)" }}
                onClick={(e) => { e.stopPropagation(); copyHex(c.hex); }}
              >
                <i
                  className={`${copied === c.hex ? "ri-check-line" : "ri-file-copy-line"} text-sm`}
                  style={{ color: getLuminance(c.hex) > 0.45 ? "#004643" : "white" }}
                />
              </div>
            </button>
          ))}
        </div>

        {/* Two Column: Adjust + Preview */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Left: Color Adjust */}
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700 rounded-2xl p-6 md:p-8" style={{ backgroundColor: "white", border: "1px solid rgba(0,70,67,0.1)" }}>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "#e8f4f1" }}>
                <i className="ri-equalizer-line text-sm" style={{ color: "#004643" }} />
              </div>
              <h3 className="text-base font-semibold" style={{ color: "#004643" }}>{copy.adjust}</h3>
            </div>

            <div className="flex items-center gap-3 sm:gap-4 mb-6 flex-wrap sm:flex-nowrap">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl shrink-0 transition-colors duration-300" style={{ backgroundColor: activeColor.hex }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: "#004643" }}>{activeColor.name}</div>
                <div className="text-xs font-mono text-gray-400">{activeColor.hex.toUpperCase()}</div>
              </div>
              <div className="text-xl text-gray-300 hidden sm:block">→</div>
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl shrink-0 relative transition-colors duration-300" style={{ backgroundColor: adjusted }}>
                <button
                  onClick={() => copyHex(adjusted)}
                  className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer rounded-xl"
                  style={{ backgroundColor: "rgba(0,0,0,0.15)" }}
                >
                  <i className={`${copied === adjusted ? "ri-check-line" : "ri-file-copy-line"} text-white text-sm`} />
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-mono font-bold truncate" style={{ color: "#004643" }}>{adjusted.toUpperCase()}</div>
                <div className="text-xs text-gray-400">{copy.modified}</div>
              </div>
            </div>

            {/* Lightness Slider */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium" style={{ color: "#004643" }}>{copy.lightness}</label>
                <span className="text-xs font-mono text-gray-400">{lightnessShift > 0 ? "+" : ""}{lightnessShift}%</span>
              </div>
              <input
                type="range"
                min="-40"
                max="40"
                value={lightnessShift}
                onChange={(e) => setLightnessShift(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, #202020, ${activeColor.hex}, white)`, accentColor: "#004643" }}
              />
            </div>

            {/* Saturation Slider */}
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium" style={{ color: "#004643" }}>{copy.saturation}</label>
                <span className="text-xs font-mono text-gray-400">{satShift > 0 ? "+" : ""}{satShift}%</span>
              </div>
              <input
                type="range"
                min="-30"
                max="30"
                value={satShift}
                onChange={(e) => setSatShift(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{ background: `linear-gradient(to right, #808080, ${activeColor.hex})`, accentColor: "#004643" }}
              />
            </div>

            <button
              onClick={() => { setLightnessShift(0); setSatShift(0); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-colors cursor-pointer"
              style={{ border: "1px solid #acd1c6", color: "#004643" }}
            >
              <i className="ri-refresh-line" />
              {copy.reset}
            </button>
          </div>

          {/* Right: Live Preview */}
          <div className="reveal opacity-0 translate-y-6 transition-all duration-700 rounded-2xl p-6 md:p-8" style={{ backgroundColor: "white", border: "1px solid rgba(0,70,67,0.1)" }}>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "#e8f4f1" }}>
                <i className="ri-eye-line text-sm" style={{ color: "#004643" }} />
              </div>
              <h3 className="text-base font-semibold" style={{ color: "#004643" }}>{copy.preview}</h3>
            </div>

            {/* Mock Business Card */}
            <div className="rounded-xl p-6 mb-5 transition-all duration-300" style={{ backgroundColor: bgColor, border: "1px solid rgba(0,70,67,0.08)" }}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold transition-colors duration-300" style={{ backgroundColor: textColor, color: bgColor }}>
                  M
                </div>
                <div>
                  <div className="text-sm font-semibold transition-colors duration-300" style={{ color: textColor }}>{copy.sampleCompany}</div>
                  <div className="text-xs transition-colors duration-300" style={{ color: textColor, opacity: 0.6 }}>{copy.sampleService}</div>
                </div>
              </div>
              <div className="space-y-1 mb-4">
                <div className="text-xs flex items-center gap-2 transition-colors duration-300" style={{ color: textColor, opacity: 0.7 }}>
                  <i className="ri-mail-line" />
                  {copy.sampleEmail}
                </div>
                <div className="text-xs flex items-center gap-2 transition-colors duration-300" style={{ color: textColor, opacity: 0.7 }}>
                  <i className="ri-phone-line" />
                  +36 30 123 4567
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <span className="px-3 py-1 rounded-full text-xs font-medium transition-colors duration-300" style={{ backgroundColor: textColor, color: bgColor }}>
                  Branding
                </span>
                <span className="px-3 py-1 rounded-full text-xs font-medium transition-colors duration-300" style={{ backgroundColor: adjusted, color: getLuminance(adjusted) > 0.45 ? "#202020" : "white" }}>
                  Grafika
                </span>
                {colors[2] && (
                  <span className="px-3 py-1 rounded-full text-xs font-medium transition-colors duration-300" style={{ backgroundColor: colors[2].hex, color: getLuminance(colors[2].hex) > 0.45 ? "#202020" : "white" }}>
                    Print
                  </span>
                )}
              </div>
            </div>

            {/* Mock Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer"
                style={{ backgroundColor: textColor, color: bgColor }}
              >
                {copy.mainButton}
              </button>
              <button
                className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all duration-300 cursor-pointer"
                style={{ backgroundColor: adjusted, color: getLuminance(adjusted) > 0.45 ? "#202020" : "white" }}
              >
                {copy.secondaryButton}
              </button>
            </div>

            {/* Color Pickers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium mb-2" style={{ color: "#004643" }}>{copy.bgColor}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0 shrink-0"
                  />
                  <span className="text-xs font-mono text-gray-500 truncate">{bgColor.toUpperCase()}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-2" style={{ color: "#004643" }}>{copy.textColor}</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={textColor}
                    onChange={(e) => setTextColor(e.target.value)}
                    className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0 shrink-0"
                  />
                  <span className="text-xs font-mono text-gray-500 truncate">{textColor.toUpperCase()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contrast Checker */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 rounded-2xl p-6 md:p-8" style={{ backgroundColor: "white", border: "1px solid rgba(0,70,67,0.1)" }}>
          <div className="flex items-center gap-2 mb-6">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "#e8f4f1" }}>
              <i className="ri-contrast-2-line text-sm" style={{ color: "#004643" }} />
            </div>
            <h3 className="text-base font-semibold" style={{ color: "#004643" }}>{copy.contrast}</h3>
            <span className="ml-auto text-xs text-gray-400">{copy.wcag}</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            {/* Preview Box */}
            <div className="rounded-xl p-8 text-center transition-all duration-300" style={{ backgroundColor: bgColor }}>
              <div className="text-2xl font-bold mb-2 transition-colors duration-300" style={{ color: textColor }}>
                {copy.headline}
              </div>
              <div className="text-sm transition-colors duration-300" style={{ color: textColor, opacity: 0.7 }}>
                {copy.paragraph}
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{copy.contrastRatio}</span>
                <span className="text-lg font-bold font-mono" style={{ color: "#004643" }}>{contrast.toFixed(2)}:1</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500">{copy.rating}</span>
                <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: `${grade.color}20`, color: grade.color }}>
                  {grade.label}
                </span>
              </div>
              <div className="text-xs text-gray-400 leading-relaxed">
                {contrast >= 4.5
                  ? copy.contrastGood
                  : contrast >= 3
                    ? copy.contrastOk
                    : copy.contrastBad}
              </div>
            </div>

            {/* Quick Pairs – derived from current palette */}
            <div className="flex flex-col gap-2">
              <div className="text-xs font-medium mb-1" style={{ color: "#004643" }}>{copy.quickPairs}</div>
              {quickPairs.map((pair) => (
                <button
                  key={pair.name}
                  onClick={() => { setBgColor(pair.bg); setTextColor(pair.text); }}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all cursor-pointer"
                  style={{ border: "1px solid rgba(0,70,67,0.08)", backgroundColor: "#f8fbfa" }}
                >
                  <div className="flex gap-1">
                    <div className="w-5 h-5 rounded-md transition-colors duration-300" style={{ backgroundColor: pair.bg, border: "1px solid rgba(0,0,0,0.08)" }} />
                    <div className="w-5 h-5 rounded-md transition-colors duration-300" style={{ backgroundColor: pair.text }} />
                  </div>
                  <span className="text-xs text-gray-600">{pair.name}</span>
                  <span className="ml-auto text-xs font-mono text-gray-400">{getContrastRatio(pair.bg, pair.text).toFixed(1)}:1</span>
                </button>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
